import React, { useState, useRef, useEffect } from "react";
import Dropdown from "./components/Dropdown";
import { Search, X, Filter, MapPin, Route, Tent, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Toolbar = ({
                     onSearch,
                     userName,
                     userId,
                     pathToAvatar,
                     canCreate,
                     isAdmin,
                     onRegionChange,
                     selectedRegion,
                     onFilterChange,
                     searchTerm = "",
                     onSearchTermChange = () => {},
                     filters = {
                         difficulty: null,
                         dogs: false,
                         private: false,
                         children: false,
                         myevents: null
                     }
                 }) => {
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);

    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const regions = [
        { value: "", label: "All Regions" },
        { value: "Pacific NorthWest", label: "Pacific NorthWest" },
        { value: "West", label: "West" },
        { value: "Southwest", label: "Southwest" },
        { value: "Mountain West", label: "Mountain West" },
        { value: "Midwest", label: "Midwest" },
        { value: "Northeast", label: "Northeast" },
        { value: "Southeast", label: "Southeast" },
        { value: "Hawaii", label: "Hawaii" },
        { value: "Canada West", label: "Canada West" },
        { value: "Canada Mid", label: "Canada Mid" },
        { value: "Canada East", label: "Canada East" },
        { value: "Baja", label: "Baja" },
        { value: "Other", label: "Other" }
    ];

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFilterDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSearch(searchTerm, selectedRegion);
    };

    const handleRegionChange = (value) => {
        onRegionChange(value);
    };

    const handleClear = () => {
        onSearchTermChange("");
        onSearch("");
    };

    const handleDifficultyChange = (difficulty) => {
        const newFilters = {
            ...filters,
            difficulty: filters.difficulty === difficulty ? null : difficulty
        };
        onFilterChange(newFilters);
    };

    const handleToggleFilter = (filterName) => {
        const newFilters = {
            ...filters,
            [filterName]: !filters[filterName]
        };
        onFilterChange(newFilters);
    };

    return (
        <div className="bg-white rounded-lg shadow-md mb-2 relative">
            {/* Main container with better spacing for tablets */}
            <div className="bg-white p-3">
                <div className="flex flex-col lg:flex-row lg:justify-center lg:items-center gap-3 lg:gap-6 max-w-7xl mx-auto">
                    {/* Content wrapper with max width and centering */}
                    {/* Top row: Navigation buttons - always full width on mobile/tablet */}
                    <div className="flex flex-wrap items-center justify-center gap-2 lg:order-1 lg:flex-nowrap lg:justify-start lg:flex-shrink-0">
                        {/* Map Data Load Button - Admin only */}
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={() => navigate("/events/mapload")}
                                className="bg-purple-600 text-white p-2.5 rounded hover:bg-purple-700 transition-colors touch-manipulation"
                                title="Load Map Data"
                            >
                                <Upload className="h-4 w-4"/>
                            </button>
                        )}

                        {/* Camping Map Button - Only show if user is logged in */}
                        {userId && (
                            <button
                                type="button"
                                onClick={() => navigate("/events/campmap")}
                                className="bg-amber-600 text-white p-2.5 rounded hover:bg-amber-700 transition-colors touch-manipulation"
                                title="Camping Map"
                            >
                                <Tent className="h-4 w-4"/>
                            </button>
                        )}

                        {/* GPX Viewer Button */}
                        <button
                            type="button"
                            onClick={() => navigate("/events/gpx")}
                            className="bg-green-500 text-white p-2.5 rounded hover:bg-green-600 transition-colors touch-manipulation"
                            title="GPX Viewer"
                        >
                            <Route className="h-4 w-4"/>
                        </button>

                        {/* Map Button */}
                        <button
                            type="button"
                            onClick={() => navigate("/events/eventsmap")}
                            className="bg-blue-500 text-white p-2.5 rounded hover:bg-blue-600 transition-colors touch-manipulation"
                            title="Events Map View"
                        >
                            <MapPin className="h-4 w-4"/>
                        </button>
                    </div>

                    {/* Middle row: Search form - responsive layout */}
                    <form onSubmit={handleSubmit} className="flex-1 lg:order-2 lg:max-w-2xl min-w-0">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mx-auto lg:mx-0">

                            {/* Region dropdown - full width on mobile, fixed width on tablet+ */}
                            <div className="w-full sm:w-auto sm:min-w-[140px] sm:flex-shrink-0">
                                <Dropdown
                                    id="region-filter"
                                    name="region"
                                    value={selectedRegion}
                                    onChange={handleRegionChange}
                                    options={regions}
                                    className="w-full border rounded px-3 py-2.5 bg-white touch-manipulation"
                                />
                            </div>

                            {/* Search input and controls */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <input
                                    type="text"
                                    placeholder="Search events..."
                                    className="flex-1 min-w-0 border rounded px-3 py-2.5 touch-manipulation"
                                    value={searchTerm}
                                    onChange={(e) => onSearchTermChange(e.target.value)}
                                />

                                {/* Action buttons */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            onClick={handleClear}
                                            className="bg-gray-200 text-gray-600 p-2.5 rounded hover:bg-gray-300 transition-colors touch-manipulation"
                                            title="Clear search"
                                        >
                                            <X className="h-4 w-4"/>
                                        </button>
                                    )}

                                    <button
                                        type="submit"
                                        className="bg-orange-500 text-white p-2.5 rounded hover:bg-orange-600 transition-colors touch-manipulation"
                                        title="Search"
                                    >
                                        <Search className="h-4 w-4"/>
                                    </button>

                                    {/* Filter button and dropdown - Fixed positioning */}
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                            className={`p-2.5 rounded transition-colors touch-manipulation ${
                                                showFilterDropdown || Object.values(filters).some(v => v)
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                            }`}
                                            title="Filter options"
                                        >
                                            <Filter className="h-4 w-4"/>
                                        </button>

                                        {/* Filter dropdown - Fixed positioning and visibility */}
                                        {showFilterDropdown && (
                                            <div className="absolute right-0 top-full mt-2 w-64 sm:w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] max-h-96 overflow-y-auto">
                                                <div className="px-4 py-3 border-b border-gray-200">
                                                    <p className="font-semibold text-sm text-gray-900 mb-2">Terrain</p>
                                                    <div className="space-y-2">
                                                        {["Easy", "Medium", "Hard"].map(difficulty => (
                                                            <label key={difficulty} className="flex items-center touch-manipulation cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters.difficulty === difficulty}
                                                                    onChange={() => handleDifficultyChange(difficulty)}
                                                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                />
                                                                <span className="text-sm text-gray-700 select-none">{difficulty}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="px-4 py-3">
                                                    <p className="font-semibold text-sm text-gray-900 mb-2">Options</p>
                                                    <div className="space-y-2">
                                                        {[
                                                            { key: "dogs", label: "Dogs Allowed" },
                                                            { key: "private", label: "Private Events" },
                                                            { key: "children", label: "Children Allowed" },
                                                            { key: "myevents", label: "My Events" }
                                                        ].map(({ key, label }) => (
                                                            <label key={key} className="flex items-center touch-manipulation cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filters[key]}
                                                                    onChange={() => handleToggleFilter(key)}
                                                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                />
                                                                <span className="text-sm text-gray-700 select-none">{label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>

                    {/* Bottom row: User info and actions - responsive layout */}
                    <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-end gap-3 lg:order-3 lg:flex-shrink-0">

                        {/* User info */}
                        {(userName || pathToAvatar) && (
                            <div className="flex items-center gap-2 order-2 sm:order-1">
                                {pathToAvatar && (
                                    <img
                                        src={pathToAvatar}
                                        alt="User avatar"
                                        width="32"
                                        height="32"
                                        className="rounded-full object-cover flex-shrink-0"
                                        loading="lazy"
                                    />
                                )}
                                {userName && (
                                    <span className="text-sm text-gray-700 whitespace-nowrap">
                                    Welcome, {userName}
                                </span>
                                )}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row items-center gap-2 order-1 sm:order-2">
                            {canCreate && (
                                <button
                                    type="button"
                                    onClick={() => navigate("/events/create-event")}
                                    className="bg-orange-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors touch-manipulation whitespace-nowrap w-full sm:w-auto"
                                >
                                    Create an Event
                                </button>
                            )}

                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => navigate("/events/create-revenue-event")}
                                    className="bg-green-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-600 transition-colors touch-manipulation whitespace-nowrap w-full sm:w-auto"
                                >
                                    Create Revenue Event
                                </button>
                            )}
                        </div>
                    </div>

                </div> {/* Close content wrapper */}
            </div> {/* Close main container */}
        </div>
    );
};

export default Toolbar;